/**
 * useDraftAutoSave — 게시글 임시 저장 훅
 *
 * IndexedDB 기반으로 작성 중인 게시글을 자동 저장합니다.
 * - 3초 디바운스로 title / editorJson / tags 를 로컬에 저장
 * - 모달 재진입 시 저장된 초안이 있으면 복원 제안
 * - 게시글 발행 성공 시 해당 보드의 초안 삭제
 *
 * IndexedDB Store: "board_drafts"
 * Key: boardId (보드별 1개 초안)
 */
import { useRef, useEffect, useCallback, useState } from 'react';
import { t } from '@lingui/macro';

const DB_NAME = 'nextpod_board';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';

export interface DraftData {
  boardId: string;
  title: string;
  editorJson: any;
  tags: string[];
  savedAt: number; // timestamp
}

/** IndexedDB 인스턴스 열기 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'boardId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** 초안 저장 */
async function saveDraft(draft: DraftData): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(draft);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/** 초안 로드 */
async function loadDraft(boardId: string): Promise<DraftData | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(boardId);
    req.onsuccess = () => { db.close(); resolve(req.result ?? null); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

/** 초안 삭제 */
async function deleteDraft(boardId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(boardId);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/** TipTap doc 이 사실상 비어있는지 — 빈 paragraph 만 있거나 텍스트 0 인 경우 true.
 *  draft 자동 저장/복원 모두에서 사용. */
function isEmptyTipTapDoc(json: any): boolean {
  if (!json || typeof json !== 'object') return true;
  if (json.type !== 'doc') return false; // 다른 형태면 콘텐츠로 간주
  const content = Array.isArray(json.content) ? json.content : [];
  if (content.length === 0) return true;
  // 모든 노드가 빈 paragraph 면 empty
  const allEmpty = content.every((node: any) => {
    if (!node || typeof node !== 'object') return true;
    if (node.type !== 'paragraph') return false; // heading/list/image 등은 콘텐츠
    const inner = Array.isArray(node.content) ? node.content : [];
    if (inner.length === 0) return true;
    return inner.every((c: any) => c?.type === 'text' && (!c.text || c.text.trim() === ''));
  });
  return allEmpty;
}

/** 저장 / 복원 가치 있는 콘텐츠인지 — title / editorJson(빈 doc 제외) / tags 중 하나라도 있으면 true */
function hasMeaningfulDraftContent(args: { title: string; editorJson: any; tags: string[] }): boolean {
  if (args.title.trim()) return true;
  if (args.tags && args.tags.length > 0) return true;
  if (args.editorJson && !isEmptyTipTapDoc(args.editorJson)) return true;
  return false;
}

/** 시간 경과 포맷 */
function formatTimeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t`just now`;
  if (mins < 60) return t`${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t`${hours}h ago`;
  const days = Math.floor(hours / 24);
  return t`${days}d ago`;
}

interface UseDraftAutoSaveOptions {
  boardId: string | undefined;
  title: string;
  editorJson: any;
  tags: string[];
  isOpen: boolean;
  onRestore: (draft: DraftData) => void;
}

/**
 * 게시글 임시 저장 훅
 *
 * @returns
 * - clearDraft: 발행 성공 시 호출하여 초안 삭제
 * - hasDraft: 복원 가능한 초안이 있는지 여부
 */
export function useDraftAutoSave({
  boardId,
  title,
  editorJson,
  tags,
  isOpen,
  onRestore,
}: UseDraftAutoSaveOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoredRef = useRef(false);
  /** 가장 최근 자동 저장 완료 시각 (인디케이터용) */
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  // ── 1) 모달 열릴 때: 저장된 초안 확인 & 복원 제안 ──
  useEffect(() => {
    if (!isOpen || !boardId) {
      restoredRef.current = false;
      return;
    }

    // 이미 복원 시도한 세션이면 스킵
    if (restoredRef.current) return;
    restoredRef.current = true;

    loadDraft(boardId).then(draft => {
      if (!draft) return;
      // 내용이 있는 초안만 복원 제안. 빈 TipTap doc 도 무시 — 모달 마운트만으로 생성된 빈 paragraph 는 콘텐츠 아님.
      if (!hasMeaningfulDraftContent(draft)) {
        deleteDraft(boardId).catch(() => {});
        return;
      }

      const timeAgo = formatTimeAgo(draft.savedAt);
      const confirmed = window.confirm(
        `${t`You have an unsaved draft (${timeAgo}).`}\n\n${t`Continue writing?`}`
      );
      if (confirmed) {
        onRestore(draft);
      } else {
        // 사용자가 거부하면 초안 삭제
        deleteDraft(boardId).catch(() => {});
      }
    }).catch(() => {});
  }, [isOpen, boardId]);

  // ── 2) 3초 디바운스 자동 저장 ──
  useEffect(() => {
    if (!isOpen || !boardId) return;

    // 제목도 본문도 없으면 저장 불필요 (빈 TipTap doc 포함)
    if (!hasMeaningfulDraftContent({ title, editorJson, tags })) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const savedAt = Date.now();
      saveDraft({
        boardId,
        title,
        editorJson,
        tags,
        savedAt,
      })
        .then(() => setLastSavedAt(savedAt))
        .catch(err => console.warn('[DraftAutoSave] 저장 실패:', err));
    }, 3000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isOpen, boardId, title, editorJson, tags]);

  // ── 3) 발행 성공 시 초안 삭제 ──
  const clearDraft = useCallback(() => {
    if (!boardId) return;
    deleteDraft(boardId).catch(() => {});
  }, [boardId]);

  return { clearDraft, lastSavedAt };
}

/** "임시 저장됨 · 방금 전" 같은 상대 시간 표시용 */
export function formatDraftSavedAgo(savedAt: number | null): string | null {
  if (!savedAt) return null;
  return formatTimeAgo(savedAt);
}
